import React, { useEffect } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { parseTokensFromHash, verifyAndClearState } from './shared/auth/okta';
import { getApiUrl } from './shared/utils/api';

const RETURN_TO_KEY = 'onesec_return_to';

/**
 * Exchange the PKCE authorization code server-side so the backend can set the
 * HttpOnly onesec_okta_token cookie (Option 3 – shared-cookie flow).
 * Returns the access_token so we can keep it in localStorage for backward compat.
 */
async function exchangeCodeViaBackend(
  code: string,
  codeVerifier: string,
  redirectUri: string,
): Promise<{ access_token: string; id_token?: string; expires_in: number }> {
  const res = await fetch(`${getApiUrl()}/api/auth/callback`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ code, code_verifier: codeVerifier, redirect_uri: redirectUri }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || `Token exchange failed: ${res.status}`);
  }
  return res.json();
}

const Callback: React.FC = () => {
  useEffect(() => {
    const processCallback = async () => {
      try {
        const search = window.location.search;
        const hash = window.location.hash;
        const searchParams = new URLSearchParams(search.replace(/^\?/, ''));

        const error =
          searchParams.get('error') ||
          new URLSearchParams(hash.replace(/^#/, '')).get('error');
        if (error) {
          const desc =
            searchParams.get('error_description') ||
            new URLSearchParams(hash.replace(/^#/, '')).get('error_description');
          alert(`Login failed: ${desc || error || 'Unknown error'}`);
          window.location.replace('/');
          return;
        }

        // ── PKCE Authorization Code flow ────────────────────────────────────
        const code = searchParams.get('code');
        const stateFromUrl = searchParams.get('state');
        if (code) {
          // Verify state CSRF token and retrieve code_verifier from sessionStorage
          const codeVerifier = verifyAndClearState(stateFromUrl ?? undefined);
          if (!codeVerifier) {
            alert('Login failed: session state mismatch. Please try again.');
            window.location.replace('/login');
            return;
          }

          const redirectUri =
            process.env.REACT_APP_OKTA_REDIRECT_URI ||
            `${window.location.origin}/login/callback`;

          // Exchange via backend (sets HttpOnly cookie) and keep token in localStorage too
          const tokenData = await exchangeCodeViaBackend(code, codeVerifier, redirectUri);
          localStorage.setItem('okta_access_token', tokenData.access_token);
          if (tokenData.id_token) localStorage.setItem('okta_id_token', tokenData.id_token);
          const expiresAt = (Date.now() + tokenData.expires_in * 1000).toString();
          localStorage.setItem('okta_expires_at', expiresAt);

          const returnTo = sessionStorage.getItem(RETURN_TO_KEY) || '/';
          sessionStorage.removeItem(RETURN_TO_KEY);
          window.history.replaceState({}, document.title, '/');
          window.location.replace(returnTo);
          return;
        }

        // ── Legacy Implicit flow ─────────────────────────────────────────────
        const tokenString = hash || search;
        if (
          tokenString &&
          tokenString.length > 1 &&
          (tokenString.includes('access_token=') || tokenString.includes('id_token='))
        ) {
          parseTokensFromHash(tokenString);
          const returnTo = sessionStorage.getItem(RETURN_TO_KEY) || '/';
          sessionStorage.removeItem(RETURN_TO_KEY);
          window.history.replaceState({}, document.title, '/');
          window.location.replace(returnTo);
          return;
        }

        // Already authenticated – just redirect
        if (localStorage.getItem('okta_access_token')) {
          window.history.replaceState({}, document.title, '/');
          window.location.replace('/');
        }
      } catch (e) {
        alert(`Login failed: ${e instanceof Error ? e.message : 'Please try again.'}`);
        window.location.replace('/');
      }
    };

    processCallback();
  }, []);

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center', justifyContent: 'center' }}>
      <CircularProgress />
      <Typography>Signing you in…</Typography>
    </Box>
  );
};

export default Callback;


